/**
 * Transform object keys from kebab-case to camelCase
 * @param {Object} obj - Object with kebab-case keys
 * @returns {Object} - New object with camelCase keys
 */
export const transformKebabToCamel = (obj) => {
  // Handle null, undefined, or non-object values
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }

  const newObj = {};
  Object.keys(obj).forEach((key) => {
    const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    newObj[camelKey] = obj[key];
  });
  return newObj;
};

/**
 * Transform object keys from snake_case to camelCase
 * @param {Object} obj - Object with snake_case keys
 * @returns {Object} - New object with camelCase keys
 */
export const transformSnakeToCamel = (obj) => {
  // Handle null, undefined, or non-object values
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }

  const newObj = {};
  Object.keys(obj).forEach((key) => {
    const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    newObj[camelKey] = obj[key];
  });
  return newObj;
};

/**
 * Transform object keys from camelCase to snake_case
 * @param {Object} obj - Object with camelCase keys
 * @returns {Object} - New object with snake_case keys
 */
export const transformCamelToSnake = (obj) => {
  // Handle null, undefined, or non-object values
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }

  const newObj = {};
  Object.keys(obj).forEach((key) => {
    const snakeKey = camelToSnake(key);
    newObj[snakeKey] = obj[key];
  });
  return newObj;
};

/**
 * Transform string from camelCase to snake_case
 * @param {String} str - String with camelCase value
 * @returns {String} - New string with snake_case value
 */
export const camelToSnake = (str) => {
  // Handle null or undefined strings
  if (!str || typeof str !== "string") {
    return str;
  }

  return str.replace(/([A-Z])/g, (g) => `_${g[0].toLowerCase()}`);
};

/**
 * Transform model to JSON with deep transformation
 * Converts all object keys from camelCase to snake_case recursively
 * @param {*} model - Model to transform (can be object, array, or primitive)
 * @returns {*} - Transformed JSON with snake_case keys
 */
export const transformModelToJson = (model) => {
  if (!model) return model; // Return null, undefined, 0, false, "" as is

  // Handle arrays
  if (Array.isArray(model)) {
    return model.map((item) => transformModelToJson(item));
  }

  // Handle objects (deep transformation)
  if (typeof model === "object" && model !== null) {
    const result = {};
    Object.keys(model).forEach((key) => {
      const value = model[key];
      const snakeKey = camelToSnake(key);
      // Recursively transform nested objects and arrays
      if (typeof value === "object" && value !== null) {
        result[snakeKey] = transformModelToJson(value);
      } else {
        result[snakeKey] = value;
      }
    });
    return result;
  }

  // Return primitive values as is
  return model;
};
